"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { buildSpellingPages } from "@/components/tools/printables/spelling-build";
import { useToolUi } from "@/components/tools/tool-ui";
import { useWorksheetSeed } from "@/components/tools/worksheets-math/worksheet-kit";
import { t } from "@/lib/i18n/interpolate";
import { type PaperSize, pageGeometry } from "@/lib/tool-kit/printables/page";
import {
  MAX_WORDS,
  parseSpellingList,
  shuffleEntries,
} from "@/lib/tool-kit/printables/spelling";

/** English strings. A translated content file overrides these through `ui`. */
export const SPELLING_TEST_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  defaultTitle: "Spelling Test",
  sampleWords:
    "because, friend, school, would, people, again, said, were, their, house",
  wordsLabel: "Spelling words",
  wordsHint:
    "One word per line, or separated by commas. Add a dictation sentence after a bar — for example: friend | My friend lives next door.",
  clear: "Clear the list",
  cappedNotice: "Only the first {max} words are used.",
  emptyNotice:
    "No words entered, so the sheets print blank: twenty numbered lines for a test, and blank practice rows.",
  sheetsLabel: "Sheets to print",
  sheetTest: "Test sheet",
  sheetPractice: "Practice sheet",
  sheetKey: "Teacher's copy",
  shuffle: "Put the test in random order",
  shuffleAgain: "Shuffle again",
  spacingLabel: "Writing space",
  spacings: {
    standard: "Standard",
    roomy: "Roomy",
    extra: "Extra roomy — younger writers",
  },
  columnsLabel: "Test columns",
  columns: { "1": "One column", "2": "Two columns" },
  writeBoxesLabel: "Write-it boxes per word",
  traceLabel: "Show a dashed word to trace in the first box",
  sentenceLinesLabel: "Dictation sentence lines at the end of the test",
  titleLabel: "Title",
  titleHint:
    "Prints on every sheet; the practice and teacher's sheets add a suffix.",
  nameLines: "Name, date and score lines",
  practiceSuffix: "Practice",
  keySuffix: "Teacher's copy",
  nameLabel: "Name",
  dateLabel: "Date",
  scoreLabel: "Score",
  stepLook: "1  Look and say",
  stepWrite: "2  Cover and write",
  stepCheck: "3  Check",
  sentenceLabel: "Sentence",
  keyInstruction:
    "Read the word, say the sentence, then say the word again. Words are in the order of the test sheet.",
  previewLabel: "Spelling sheets preview",
} as const;

const SPACING = {
  standard: { test: 10, practice: 12 },
  roomy: { test: 13, practice: 15 },
  extra: { test: 16, practice: 18 },
} as const;
const SENTENCE_CHOICES = ["0", "1", "2", "3", "4"];
const WRITE_CHOICES = ["1", "2", "3"];

type Spacing = keyof typeof SPACING;

export default function SpellingTestGeneratorWidget() {
  const strings = useToolUi(SPELLING_TEST_GENERATOR_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const seed = useWorksheetSeed();
  const [text, setText] = useState<string>(strings.sampleWords);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [title, setTitle] = useState<string>(strings.defaultTitle);
  const [nameLines, setNameLines] = useState(true);
  const [sheets, setSheets] = useState({
    test: true,
    practice: true,
    key: true,
  });
  const [shuffle, setShuffle] = useState(false);
  const [spacing, setSpacing] = useState<Spacing>("standard");
  const [columns, setColumns] = useState<"1" | "2">("1");
  const [writeBoxes, setWriteBoxes] = useState("2");
  const [trace, setTrace] = useState(false);
  const [sentenceLines, setSentenceLines] = useState("0");

  const geometry = pageGeometry(paper, "portrait");
  const parsed = parseSpellingList(text, MAX_WORDS + 1);
  const capped = parsed.length > MAX_WORDS;
  const words = parsed.slice(0, MAX_WORDS);
  const empty = words.length === 0;
  const ordered = shuffle ? shuffleEntries(words, seed.seed) : words;
  const heights = SPACING[spacing];
  const trimmed = title.trim();

  const pages = buildSpellingPages({
    geometry,
    measure,
    strings,
    title: trimmed,
    nameLines,
    words,
    ordered,
    empty,
    spacing: heights,
    columns: columns === "2" ? 2 : 1,
    sentenceLines: Number(sentenceLines),
    writeBoxes: Number(writeBoxes),
    trace,
    sheets,
  });
  const printable = pages.length > 0;

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <TextAreaField
          hint={strings.wordsHint}
          id="st-words"
          label={strings.wordsLabel}
          onChange={setText}
          prose
          rows={6}
          value={text}
        />
        <div className="flex flex-wrap gap-2">
          <button
            className={BUTTON_SMALL}
            onClick={() => setText("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        {capped ? (
          <Notice>{t(strings.cappedNotice, { max: MAX_WORDS })}</Notice>
        ) : null}
        {empty ? <Notice>{strings.emptyNotice}</Notice> : null}
        <fieldset className="space-y-2">
          <legend className="font-medium text-foreground text-sm">
            {strings.sheetsLabel}
          </legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Checkbox
              checked={sheets.test}
              label={strings.sheetTest}
              onChange={(test) => setSheets((s) => ({ ...s, test }))}
            />
            <Checkbox
              checked={sheets.practice}
              label={strings.sheetPractice}
              onChange={(practice) => setSheets((s) => ({ ...s, practice }))}
            />
            <Checkbox
              checked={sheets.key}
              label={strings.sheetKey}
              onChange={(key) => setSheets((s) => ({ ...s, key }))}
            />
          </div>
        </fieldset>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <Checkbox
            checked={shuffle}
            label={strings.shuffle}
            onChange={setShuffle}
          />
          {shuffle ? (
            <button
              className={BUTTON_SMALL}
              onClick={seed.regenerate}
              type="button"
            >
              {strings.shuffleAgain}
            </button>
          ) : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="st-spacing"
            label={strings.spacingLabel}
            onChange={setSpacing}
            options={(["standard", "roomy", "extra"] as const).map((value) => ({
              value,
              label: strings.spacings[value],
            }))}
            value={spacing}
          />
          <SelectField
            id="st-columns"
            label={strings.columnsLabel}
            onChange={setColumns}
            options={(["1", "2"] as const).map((value) => ({
              value,
              label: strings.columns[value],
            }))}
            value={columns}
          />
          <SelectField
            id="st-sentences"
            label={strings.sentenceLinesLabel}
            onChange={setSentenceLines}
            options={SENTENCE_CHOICES.map((value) => ({ value, label: value }))}
            value={sentenceLines}
          />
          <SelectField
            id="st-write"
            label={strings.writeBoxesLabel}
            onChange={setWriteBoxes}
            options={WRITE_CHOICES.map((value) => ({ value, label: value }))}
            value={writeBoxes}
          />
          <PaperControls
            idPrefix="st"
            onPaper={setPaper}
            paper={paper}
            strings={strings}
          />
        </div>
        <TextField
          hint={strings.titleHint}
          id="st-title"
          label={strings.titleLabel}
          onChange={setTitle}
          value={title}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={nameLines}
            label={strings.nameLines}
            onChange={setNameLines}
          />
          <Checkbox
            checked={trace}
            label={strings.traceLabel}
            onChange={setTrace}
          />
        </div>
        <PrintBar
          disabled={!printable}
          fileName={`spelling-test-${paper}`}
          pageTotal={pages.length}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
